import { StickyCard } from '@/components/Profile/partials/StyledComponents'
import { EmtAvatar, EmtChip, EmtIcon, EmtPaper, EmtPaperProps, EmtTypography, useMobile } from 'emt-components-v2'
import Box from '@mui/material/Box'
import PetsSubView from '@/components/EMT/PetsSubView'
import React from 'react'
import { UserCard } from '@/dataV3/Search/UserCard'
import { useTranslation } from 'next-i18next'
import ContactButton from '@/components/Profile/partials/ProfileSidebar/ContactButton'
import FavoriteButton from '@/components/Profile/partials/ProfileSidebar/FavoriteButton'
import { GenericProfile } from '@/dataV3/GenericProfile'

export type ProfileSideBarUserCard = Pick<
  UserCard,
  'displayName' | 'id' | 'distance' | 'thumbnail' | 'isPremium' | 'isCertified' | 'isFavorite' | 'ownerId' | 'animals'
> &
  Pick<GenericProfile, '@review'>

interface Props extends EmtPaperProps {
  profile: ProfileSideBarUserCard
  onContactButtonClick: () => void | Promise<void>
  isFavoriteLoading: boolean
  onFavoriteButtonClick: () => void | Promise<void>
}

const ProfileSidebarStickyCard: React.FC<Props> = ({
  profile,
  onContactButtonClick,
  isFavoriteLoading,
  onFavoriteButtonClick,
  ...props
}) => {
  const { t: tc } = useTranslation('common')
  const [isTablet] = useMobile()

  const distance = profile?.distance !== undefined ? `${Math.round(Number(profile.distance) * 10) / 10} km` : undefined

  return (
    <EmtPaper
      elevation={0}
      {...props}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        rowGap: '18px',
        padding: '20px',
        boxShadow: '0px 8px 12px rgba(0, 0, 0, 0.08), 0px 4px 48px rgba(0, 0, 0, 0.08)',
        ...props?.sx,
      }}
    >
      <StickyCard>
        <EmtAvatar size="large" src={profile.thumbnail} variant="rounded" sx={{ width: 86, height: 86 }} />
        <Box>
          <EmtTypography
            variant="h5"
            fontWeight={500}
            noWrap
            sx={{
              maxWidth: isTablet ? '95px' : '180px',
            }}
          >
            <b>{profile.displayName}</b>
          </EmtTypography>
          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <EmtIcon className="emt-pin-1" sx={{ color: 'greyGradient.light' }} />

            <EmtTypography variant="body2" color="greyGradient.light">
              {distance}
            </EmtTypography>
          </Box>

          <Box sx={{ display: isTablet ? 'block' : 'flex', gap: '5px' }}>
            {profile.isCertified && (
              <EmtChip label={tc('profileCard.idChecked')} color="successlight" sx={{ fontWeight: 600 }} />
            )}
            {profile.isPremium && (
              <EmtChip
                label={tc('profileCard.premium')}
                color="secondarylight"
                icon={<EmtIcon className="emt-crown" sx={{ fontSize: '12px' }} />}
                sx={{
                  fontWeight: 600,
                  marginTop: isTablet && profile.isCertified ? '5px' : 0,
                }}
              />
            )}
          </Box>
        </Box>
      </StickyCard>

      <ContactButton onClick={onContactButtonClick} />
      <FavoriteButton onClick={onFavoriteButtonClick} loader={isFavoriteLoading} isFavorite={profile.isFavorite} />

      {profile.animals?.length ? <PetsSubView pets={profile.animals} sx={{ mb: 2 }} /> : undefined}
    </EmtPaper>
  )
}

export default ProfileSidebarStickyCard
